import StartupPageTitle from '../../../components/StartupPageTitle';
import { FolderOpen as PageHeadingIcon } from '@phosphor-icons/react';
/**
 * DataroomView.jsx — Iteration 45. Detail view for one of the 12 datarooms.
 * Shows typed slots (upload + remove per slot) and an inline "Create share link"
 * modal + a real-time activity feed for any selected link.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import './vault.css';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, UploadSimple, Trash, LinkSimple, ClockCounterClockwise,
  Copy, Eye, DownloadSimple, ShieldCheck, Check, Lock, ListChecks,
} from '@phosphor-icons/react';

const ACCENT = '#0d9488';

const EXPIRY_PRESETS = [
  { label: '1 hour',  seconds: 60 * 60 },
  { label: '24 hours',seconds: 60 * 60 * 24 },
  { label: '7 days',  seconds: 60 * 60 * 24 * 7 },
  { label: '30 days', seconds: 60 * 60 * 24 * 30 },
];

const fmtBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const DataroomView = () => {
  const { dataroomId } = useParams();
  const navigate = useNavigate();
  const [taxonomy, setTaxonomy] = useState([]);
  const [files, setFiles] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [activeLink, setActiveLink] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, v, l] = await Promise.all([
        api.get('/vault/structure'),
        api.get('/vault'),
        api.get('/vault/links'),
      ]);
      setTaxonomy(t.data.datarooms || []);
      setFiles(v.data.files || []);
      setLinks((l.data.links || []).filter((x) => x.dataroom_id === dataroomId));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dataroomId]);

  const dataroom = useMemo(() => taxonomy.find((d) => d.id === dataroomId), [taxonomy, dataroomId]);
  const filesByDataroom = useMemo(() => files.filter((f) => f.dataroom_id === dataroomId), [files, dataroomId]);
  const filesBySlot = useMemo(() => {
    const m = {};
    filesByDataroom.forEach((f) => { (m[f.slot_id] = m[f.slot_id] || []).push(f); });
    return m;
  }, [filesByDataroom]);

  const uploadToSlot = async (slot, fileObj) => {
    if (!fileObj) return;
    try {
      const fd = new FormData(); fd.append('file', fileObj);
      const up = await api.post('/files/upload', fd);
      await api.post('/vault/files', {
        dataroom_id: dataroomId, slot_id: slot.id,
        file_id: up.data.file_id, filename: fileObj.name,
      });
      toast.success(`Uploaded ${fileObj.name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const removeFile = async (vf) => {
    if (!window.confirm(`Delete ${vf.filename}?`)) return;
    try { await api.delete(`/vault/files/${vf.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const downloadFile = async (vf) => {
    try {
      const r = await api.get(`/files/${vf.file_id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = vf.filename || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Download failed'); }
  };

  const revokeLink = async (link) => {
    if (!window.confirm(`Revoke link to "${link.recipient_label || 'unlabelled'}"?`)) return;
    try { await api.delete(`/vault/links/${link.id}`); toast.success('Revoked'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="p-6 text-sm text-text-muted">Loading dataroom…</div>;
  if (!dataroom) return <div className="p-6 text-sm text-danger">Unknown dataroom.</div>;

  return (
    <div className="fb-vault vault-detail" data-testid="dataroom-view">
      <div className="vault-shell">
        <button onClick={() => navigate('/startup/vault')} className="text-xs text-text-muted hover:text-text-primary inline-flex items-center gap-1 mb-3" data-testid="dataroom-back">
          <ArrowLeft size={16} weight="bold" /> Back to vault
        </button>

        <div className="bg-surface border border-border rounded-2xl p-5 mb-5 startup-page-header flex items-start gap-3 flex-wrap" data-testid="dataroom-hero">
          <div className="flex-1 min-w-[260px]">
            <div className="text-xs uppercase tracking-[0.22em] font-bold mb-1" style={{ color: ACCENT }}>Dataroom</div>
            <StartupPageTitle icon={PageHeadingIcon} className="text-2xl font-heading font-bold leading-[1.1]" style={{ letterSpacing: '-0.02em' }}>{dataroom.name}</StartupPageTitle>
            <p className="text-sm text-text-secondary mt-1 max-w-2xl">{dataroom.blurb}</p>
          </div>
          <button onClick={() => setShareOpen(true)} data-testid="dataroom-share-open" className="vault-button primary">
            <LinkSimple size={16} weight="bold" /> Create share link
          </button>
        </div>

        {/* Share links for this dataroom */}
        {links.length > 0 && (
          <div className="mb-5" data-testid="dataroom-links">
            <h2 className="text-xs uppercase tracking-[0.18em] font-bold text-text-muted mb-2">Share links for this dataroom</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {links.map((l) => (
                <div key={l.id} className="bg-surface border border-border rounded-xl p-3" data-testid={`dataroom-link-${l.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary truncate">{l.recipient_label || 'Unlabelled'}</div>
                      <div className="text-xs text-text-muted">Created {new Date(l.created_at).toLocaleString('en-IN')}</div>
                    </div>
                    <span className="text-xs capitalize font-medium px-2 py-1 rounded" style={{ backgroundColor: l.status === 'active' ? '#10b9811a' : '#64748b1a', color: l.status === 'active' ? '#10b981' : '#64748b' }}>{l.status}</span>
                  </div>
                  <div className="flex gap-3 text-xs text-text-muted mb-2">
                    <span className="inline-flex items-center gap-0.5"><Eye size={14} weight="bold" /> {l.view_count}</span>
                    <span className="inline-flex items-center gap-0.5"><DownloadSimple size={14} weight="bold" /> {l.download_count}</span>
                    <span className="inline-flex items-center gap-0.5"><ClockCounterClockwise size={14} weight="bold" /> {new Date(l.expires_at).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { navigator.clipboard?.writeText(l.share_url || ''); toast.success('Copied'); }} className="vault-button"><Copy size={14} weight="bold" /> Copy</button>
                    <button onClick={() => setActiveLink(l)} data-testid={`dataroom-link-activity-${l.id}`} className="vault-button"><ListChecks size={14} weight="bold" /> Activity</button>
                    {l.status === 'active' && <button onClick={() => revokeLink(l)} className="text-xs px-2 py-1 rounded border border-border hover:border-danger hover:text-danger ml-auto"><Trash size={14} weight="bold" /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Slots */}
        <h2 className="text-xs uppercase tracking-[0.18em] font-bold text-text-muted mb-2">{dataroom.slots.length} document slots</h2>
        <div className="space-y-2" data-testid="dataroom-slots">
          {dataroom.slots.map((slot) => {
            const slotFiles = filesBySlot[slot.id] || [];
            return (
              <div key={slot.id} className="bg-surface border border-border rounded-xl p-3" data-testid={`dataroom-slot-${slot.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {slotFiles.length > 0 ? <span className="w-5 h-5 rounded-full inline-flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${ACCENT}1a`, color: ACCENT }}><Check size={16} weight="bold" /></span> : <span className="w-5 h-5 rounded-full inline-flex items-center justify-center flex-shrink-0 border border-dashed border-border" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary truncate">{slot.name}</div>
                      <div className="text-xs text-text-muted">{slotFiles.length === 0 ? 'No file yet' : `${slotFiles.length} file${slotFiles.length === 1 ? '' : 's'}`}</div>
                    </div>
                  </div>
                  {!dataroom.read_only && (
                    <label className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg cursor-pointer border border-border hover:border-text-primary" data-testid={`dataroom-slot-upload-${slot.id}`}>
                      <UploadSimple size={16} weight="bold" /> Upload
                      <input type="file" className="hidden" onChange={(e) => { uploadToSlot(slot, e.target.files?.[0]); e.target.value = ''; }} />
                    </label>
                  )}
                </div>
                {dataroom.read_only && slotFiles.length === 0 && (
                  <div className="text-xs text-text-muted">
                    Documents appear here automatically once a service partner delivers a completed request.
                  </div>
                )}
                {slotFiles.length > 0 && (
                  <div className="space-y-1">
                    {slotFiles.map((f) => (
                      <div key={f.id} className="bg-background border border-border rounded-lg px-3 py-2 flex items-center gap-2 text-xs" data-testid={`dataroom-file-${f.id}`}>
                        <FileText size={16} weight="bold" className="text-text-muted flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{f.filename}</div>
                          {f.source === 'service_request' && (
                            <div className="text-xs truncate" style={{ color: ACCENT }} data-testid={`dataroom-file-source-${f.id}`}>
                              From {f.source_vendor || 'your partner'}{f.source_name ? ` \u00b7 ${f.source_name}` : ''}
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-text-muted">{fmtBytes(f.size)}</span>
                        <span className="text-xs text-text-muted">{new Date(f.created_at).toLocaleDateString('en-IN')}</span>
                        <button onClick={() => downloadFile(f)} className="text-text-muted hover:text-text-primary" data-testid={`dataroom-file-download-${f.id}`} title="Download"><DownloadSimple size={16} weight="bold" /></button>
                        <button onClick={() => removeFile(f)} aria-label={`Delete ${f.filename}`} className="text-text-muted hover:text-danger" data-testid={`dataroom-file-delete-${f.id}`}><Trash size={16} weight="bold" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {shareOpen && <ShareModal dataroomId={dataroomId} dataroomName={dataroom.name} onClose={() => setShareOpen(false)} onSaved={() => { setShareOpen(false); load(); }} />}
      {activeLink && <ActivityModal link={activeLink} onClose={() => setActiveLink(null)} />}
    </div>
  );
};

const ShareModal = ({ dataroomId, dataroomName, onClose, onSaved }) => {
  const [passcode, setPasscode] = useState('');
  const [expirySec, setExpirySec] = useState(EXPIRY_PRESETS[1].seconds);   // 24h default
  const [customExpiry, setCustomExpiry] = useState('');
  const [recipient, setRecipient] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async () => {
    if (!/^[0-9]{4}$/.test(passcode)) { toast.error('Passcode must be exactly 4 digits'); return; }
    setBusy(true);
    try {
      const payload = { dataroom_id: dataroomId, passcode, recipient_label: recipient };
      if (customExpiry) payload.expires_at = new Date(customExpiry).toISOString();
      else payload.expiry_seconds = expirySec;
      const r = await api.post('/vault/links', payload);
      setCreated(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
    <DialogContent className="fb-vault vault-upload-dialog" data-testid="vault-share-modal">
      <DialogTitle className="sr-only">New share link</DialogTitle>
      <DialogDescription className="sr-only">Manage secure access to your dataroom.</DialogDescription>
        {created ? (
          <>
            <div className="text-center mb-4">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 inline-flex items-center justify-center" style={{ backgroundColor: `${ACCENT}1a`, color: ACCENT }}><ShieldCheck size={22} weight="fill" /></div>
              <h3 className="font-heading font-bold text-lg">Share link ready</h3>
              <p className="text-xs text-text-secondary mt-1">Send the URL + the 4-digit passcode separately for max security.</p>
            </div>
            <div className="bg-background border border-border rounded-lg p-3 mb-3" data-testid="vault-share-url">
              <div className="text-xs uppercase tracking-wider font-bold text-text-muted mb-1">Share URL</div>
              <div className="text-xs font-mono break-all">{created.share_url}</div>
              <button onClick={() => { navigator.clipboard?.writeText(created.share_url); toast.success('Copied'); }} data-testid="vault-share-url-copy" className="mt-2 text-xs inline-flex items-center gap-1 px-2 py-1 rounded border border-border hover:border-text-primary"><Copy size={14} weight="bold" /> Copy URL</button>
            </div>
            <div className="bg-background border border-border rounded-lg p-3 mb-4">
              <div className="text-xs uppercase tracking-wider font-bold text-text-muted mb-1">Passcode</div>
              <div className="font-mono font-bold text-2xl tracking-[0.3em]" style={{ color: ACCENT }}>{passcode}</div>
            </div>
            <button onClick={onSaved} className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}>Done</button>
          </>
        ) : (
          <>
            <div className="flex justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">New share link</h3>
              
            </div>
            <p className="text-xs text-text-muted mb-4">Recipients land on a Founderboard-branded page, enter the passcode, and download files from <strong className="text-text-primary">{dataroomName}</strong>.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-text-muted mb-1.5">4-digit passcode *</label>
                <input value={passcode} maxLength={4} inputMode="numeric" onChange={(e) => setPasscode(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))} placeholder="e.g. 4242" data-testid="vault-share-passcode" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-lg font-mono tracking-[0.4em] text-center" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-text-muted mb-1.5">Expires in</label>
                <div className="flex gap-1.5 flex-wrap">
                  {EXPIRY_PRESETS.map((p) => (
                    <button key={p.seconds} onClick={() => { setExpirySec(p.seconds); setCustomExpiry(''); }} data-testid={`vault-share-expiry-${p.seconds}`} className={`text-xs px-2.5 py-1.5 rounded-lg font-semibold ${expirySec === p.seconds && !customExpiry ? 'text-white' : 'border border-border text-text-secondary'}`} style={expirySec === p.seconds && !customExpiry ? { backgroundColor: ACCENT } : {}}>{p.label}</button>
                  ))}
                </div>
                <div className="mt-2">
                  <label className="block text-xs uppercase tracking-wider font-bold text-text-muted mb-1">Or custom date</label>
                  <input type="datetime-local" value={customExpiry} onChange={(e) => setCustomExpiry(e.target.value)} data-testid="vault-share-custom-expiry" className="w-full bg-background border border-border rounded-lg px-3 py-1.5 text-xs" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider font-bold text-text-muted mb-1.5">Recipient label (your reference)</label>
                <input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="e.g. Bob — investor due-diligence" data-testid="vault-share-label" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
              </div>
              <button onClick={submit} disabled={busy} data-testid="vault-share-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
                <Lock size={16} weight="bold" /> {busy ? 'Creating…' : 'Generate share link'}
              </button>
            </div>
          </>
        )}
    </DialogContent>
    </Dialog>
  );
};

export const ActivityModal = ({ link, onClose }) => {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/vault/links/${link.id}/activity`);
        setActivity(r.data.activity || []);
      } catch (e) { toast.error('Failed to load'); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);
  const eventLabel = { view: 'Opened page', passcode_failed: 'Wrong passcode', passcode_success: 'Unlocked', download: 'Downloaded' };
  const eventColor = { view: '#64748b', passcode_failed: '#ef4444', passcode_success: '#10b981', download: '#6366f1' };
  return (
    <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
    <DialogContent className="fb-vault vault-upload-dialog" data-testid="vault-activity-modal">
      <DialogTitle className="sr-only">Share link activity</DialogTitle>
      <DialogDescription className="sr-only">Manage secure access to your dataroom.</DialogDescription>
        <div className="flex justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-lg">Activity</h3>
            <p className="text-xs text-text-muted">{link.recipient_label || 'Unlabelled'} · {link.view_count} views · {link.download_count} downloads</p>
          </div>
          
        </div>
        {loading ? <div className="text-sm text-text-muted">Loading…</div> : activity.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-6 text-center text-xs text-text-muted">No activity yet. Recipients haven&rsquo;t opened the link.</div>
        ) : (
          <div className="space-y-1.5">
            {activity.map((a) => (
              <div key={a.id} className="bg-background border border-border rounded-lg p-2.5 flex items-center gap-3 text-xs" data-testid={`vault-activity-row-${a.id}`}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: eventColor[a.event] || '#64748b' }} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-text-primary">{eventLabel[a.event] || a.event} {a.filename ? <span className="text-text-muted font-normal">— {a.filename}</span> : null}</div>
                  <div className="text-xs text-text-muted truncate">{a.ip}{a.user_agent ? ` · ${a.user_agent.slice(0, 50)}` : ''}</div>
                </div>
                <div className="text-xs text-text-muted font-mono">{new Date(a.timestamp).toLocaleString('en-IN')}</div>
              </div>
            ))}
          </div>
        )}
    </DialogContent>
    </Dialog>
  );
};

export default DataroomView;
